import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowRight, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error("Too many attempts. Try again in a few minutes.");
      } else {
        // Even on error, respond generically — never leak enumeration
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
      <Card className="relative w-full max-w-md bg-slate-900/90 border-slate-800 p-8 rounded-2xl" data-testid="forgot-password-card">
        <div className="mb-8 flex justify-center"><Logo size="lg" /></div>
        {!sent ? (
          <>
            <h1 className="font-display text-2xl font-bold mb-2">Forgot password?</h1>
            <p className="text-sm text-slate-400 mb-6">
              Enter the email you use for RoofLead and we'll send a reset link that's valid for 1 hour.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <Label htmlFor="email" className="text-slate-300">Email</Label>
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 bg-slate-800 border-slate-700 text-white"
                  required
                  autoFocus
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold h-11">
                {loading ? "Sending…" : <>Send reset link <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <h1 className="font-display text-2xl font-bold mb-2">Check your inbox</h1>
            <p className="text-sm text-slate-400 mb-2">
              If an account exists for <span className="text-orange-300 font-medium">{email}</span>, we just sent reset instructions.
            </p>
            <p className="text-xs text-slate-500 mb-6 flex items-center justify-center gap-1">
              <Mail className="w-3.5 h-3.5" /> Give it a minute — also check your spam folder.
            </p>
          </div>
        )}
        <p className="text-sm text-slate-400 mt-6 text-center">
          Remembered it? <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium" data-testid="forgot-goto-login">Back to login</Link>
        </p>
      </Card>
    </div>
  );
}
